import { cn } from '@repo/tailwind-config/tailwind.functions'
import { daysOfWeek } from '@repo/toolkit/constants/calendar'
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isEqual,
  isToday,
  parse,
  startOfMonth,
  startOfWeek
} from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'

import useTranslate from '../../../../../apps/backoffice/src/lib/hooks/use-translate'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../atoms/tooltip'
import Typography from '../../atoms/typography'
import FullCalendarHeader from './shared/full-calendar-header'
import { FullCalendarEvent, FullCalendarEventData } from './types'
import { sortEvents } from './utils'

type FullCalendarProps = {
  events: FullCalendarEvent[]
  headerActions?: React.ReactNode
  allowedDates?: Date[]
  disabledDates?: Date[]
  disabledDays?: number[]
  disabledDaysOfWeek?: number[]
  onEventClicked?: (event: FullCalendarEvent) => void
  onDaySelected?: (day: Date) => void
  onEventsChanged?: (events: FullCalendarEvent[]) => void
}

const FullCalendar: React.FC<FullCalendarProps> = ({
  events,
  headerActions,
  allowedDates,
  disabledDates,
  disabledDays,
  disabledDaysOfWeek,
  onEventClicked,
  onDaySelected,
  onEventsChanged
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [draggedEvent, setDraggedEvent] = useState<FullCalendarEventData>()
  const [draggedDate, setDraggedDate] = useState<Date>()
  const [calendarEvents, setCalendarEvents] = useState<FullCalendarEventData[]>([])
  const calendarEventsRef = useRef<FullCalendarEventData[]>([])
  const [dragOverCell, setDragOverCell] = useState<Date>()
  const [hoveredEventId, setHoveredEventId] = useState<string>()

  const { currentLanguage } = useTranslate()

  const resizingEventRef = useRef<FullCalendarEventData | null>(null) // Ref to track resizing event
  const newResizingEventRef = useRef<FullCalendarEventData | null>(null) // Ref to track resizing event
  const isResizingRef = useRef<boolean>(false) // avoid drag interactions during resizing
  const suppressClickRef = useRef<boolean>(false) // swallow the click the browser fires right after a resize mouseup
  const [toggleEvents, setToggleEvents] = useState(false)
  const calendarRef = useRef<HTMLDivElement | null>(null) // Ref to track calendar dimensions

  // Calculate the start and end of the month
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)

  // Get the first and last day displayed on the calendar (including previous/next month days)
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }) // Start week on Monday
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 }) // End week on Sunday

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.effectAllowed = 'move'
    // Create a small, invisible canvas
    const canvas = document.createElement('canvas')
    canvas.width = 0
    canvas.height = 0
    e.dataTransfer.setDragImage(canvas, 0, 0) // Set canvas as drag image
  }

  // Generate days for the entire grid
  // Generate days for the entire grid (only remove next month days)
  const days: Date[] = []
  let day = startDate
  while (day <= endDate) {
    // Add the day if it's in the current month or before the current month ends
    days.push(day)
    day = addDays(day, 1)
  }

  // Move to next month
  const nextMonth = () => {
    setCurrentDate(addDays(endOfMonth(currentDate), 1))
  }

  // Move to previous month
  const prevMonth = () => {
    setCurrentDate(addDays(startOfMonth(currentDate), -1))
  }

  // Handle dragging start
  const onDragStart = (event: FullCalendarEventData, day: Date) => {
    if (event.disableDragging) return
    setDraggedEvent(event)
    setDraggedDate(day)
  }

  const handleCalendarEventsChange = (events: FullCalendarEventData[]) => {
    const sorted = sortEvents(events)
    setCalendarEvents(sorted)
    calendarEventsRef.current = sorted
  }

  const compareDates = (a: Date, b: Date) => {
    return format(a, 'yyyy-MM-dd') === format(b, 'yyyy-MM-dd')
  }

  const isEventVisibleOnDay = (event: FullCalendarEventData, day: Date) =>
    compareDates(day, event.startDate) ||
    compareDates(day, event.endDate) ||
    (isAfter(day, event.startDate) && isBefore(day, event.endDate))

  const shouldRenderEventSegment = (event: FullCalendarEventData, day: Date) =>
    compareDates(day, event.startDate) ||
    (isEventVisibleOnDay(event, day) && compareDates(day, startOfWeek(day, { weekStartsOn: 1 })))

  const getWeekEvents = (day: Date) => {
    const weekStart = startOfWeek(day, { weekStartsOn: 1 })
    const weekEnd = endOfWeek(day, { weekStartsOn: 1 })

    return calendarEvents
      .filter(
        // any event overlapping the week, including ones fully inside it
        (calendarEvent) =>
          !isAfter(calendarEvent.startDate, weekEnd) && !isBefore(calendarEvent.endDate, weekStart)
      )
      .sort((a, b) => {
        const aStartsThisWeek =
          (isAfter(a.startDate, addDays(weekStart, -1)) || compareDates(a.startDate, weekStart)) &&
          (isBefore(a.startDate, addDays(weekEnd, 1)) || compareDates(a.startDate, weekEnd))
        const bStartsThisWeek =
          (isAfter(b.startDate, addDays(weekStart, -1)) || compareDates(b.startDate, weekStart)) &&
          (isBefore(b.startDate, addDays(weekEnd, 1)) || compareDates(b.startDate, weekEnd))

        if (aStartsThisWeek !== bStartsThisWeek) return aStartsThisWeek ? -1 : 1
        return a.startDate.getTime() - b.startDate.getTime()
      })
  }

  const getEventWeekPosition = (event: FullCalendarEventData, day: Date) => {
    const weekEvents = getWeekEvents(day)
    return weekEvents.findIndex((calendarEvent) => calendarEvent.id === event.id) + 1
  }

  const getWeekHeight = (day: Date) => Math.max(128, getWeekEvents(day).length * 50 + 40)

  // Handle drop event on a new day
  const onDrop = async () => {
    const latest = calendarEventsRef.current
    const oldEvt = draggedEvent
    const newDraggedEvent = latest.find((event) => event.id === draggedEvent?.id)
    if (newDraggedEvent && oldEvt) {
      const oldEvents = latest.map((event) => (event.id === oldEvt.id ? oldEvt : event))
      // commit and notify
      handleCalendarEventsChange(latest)
      const sortedEvents = sortEvents(latest)
      onEventsChanged?.(sortedEvents)
      const result = await oldEvt.onUpdate?.(oldEvt, newDraggedEvent)
      if (result === false) {
        // revert the changes
        handleCalendarEventsChange(oldEvents)
        const sortedEvents = sortEvents(oldEvents)
        onEventsChanged?.(sortedEvents)
      }
      setDraggedEvent(undefined)
      setDraggedDate(undefined)
      setDragOverCell(undefined)
    }
  }

  const onDragOver = (day: Date) => {
    if (draggedEvent && draggedDate) {
      const initialStartDate = new Date(draggedEvent.startDate) // Keep the original startDate
      const initialEndDate = new Date(draggedEvent.endDate) // Keep the original endDate
      const daysBetween = differenceInCalendarDays(day, draggedDate) // Calculate the difference

      // Preview how the event would change if dropped here
      const previewEvents = calendarEvents.map((event) => {
        if (event.id === draggedEvent.id) {
          return {
            ...event,
            startDate: addDays(initialStartDate, daysBetween),
            endDate: addDays(initialEndDate, daysBetween)
          }
        }
        return event
      })

      handleCalendarEventsChange(previewEvents)
    }
  }

  // Handle mouse movement for resizing
  const handleMouseMove = (e: MouseEvent) => {
    const resizingEvent = resizingEventRef.current
    if (resizingEvent && calendarRef.current) {
      // Get all cells (assuming they are uniquely identified with `cell-` id)
      const cells = document.querySelectorAll('[id^="cell-"]')

      // Iterate through each cell and check if the mouse is inside its bounds
      cells.forEach((cell) => {
        const rect = cell.getBoundingClientRect()
        const mouseX = e.clientX
        const mouseY = e.clientY

        // Check if the mouse is within the bounds of the cell
        if (
          mouseX >= rect.left &&
          mouseX <= rect.right &&
          mouseY >= rect.top &&
          mouseY <= rect.bottom
        ) {
          const cellId = cell.id
          const dateStr = cellId.split('cell-')[1]
          const newDate = parse(dateStr, 'yyyy-MM-dd', new Date()) // Extract date with local parse

          // Only update if newDate is after startDate
          if (isAfter(newDate, addDays(resizingEvent.startDate, -1))) {
            const newEvent = { ...resizingEvent, endDate: newDate }
            newResizingEventRef.current = newEvent
            const updatedEvents = calendarEvents.map((event) =>
              event.id === resizingEvent.id ? newEvent : event
            )
            // Update local preview only during resize; parent will be notified on mouseup
            handleCalendarEventsChange(updatedEvents)
          }
        }
      })
    }
  }

  // Stop resizing the event
  const stopResize = async () => {
    // the synthetic click fires in the same task as this mouseup, so clear the guard right after it
    setTimeout(() => {
      suppressClickRef.current = false
    }, 0)
    const newEvent = newResizingEventRef.current
    const oldEvent = resizingEventRef.current
    try {
      if (newEvent && oldEvent) {
        const result = await newEvent.onUpdate?.(oldEvent, newEvent)
        if (result === false) {
          // revert
          const reverted = calendarEventsRef.current.map((ev) =>
            ev.id === oldEvent.id ? oldEvent : ev
          )
          handleCalendarEventsChange(reverted)
          onEventsChanged?.(sortEvents(reverted))
        } else {
          // commit resized endDate and notify once
          const committed = calendarEventsRef.current.map((ev) =>
            ev.id === oldEvent.id ? newEvent : ev
          )
          handleCalendarEventsChange(committed)
          onEventsChanged?.(sortEvents(committed))
        }
      }
    } finally {
      resizingEventRef.current = null
      newResizingEventRef.current = null
      isResizingRef.current = false
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }

  // Start resizing the event
  const startResize = (event: FullCalendarEventData) => {
    if (event.disableExtend) return
    isResizingRef.current = true
    suppressClickRef.current = true
    resizingEventRef.current = event // Set the resizing event in the ref
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)
  }

  const isDisabledDay = (day: Date) =>
    (allowedDates && !allowedDates?.some((date) => compareDates(date, day))) ||
    disabledDates?.some((date) => compareDates(date, day)) ||
    disabledDays?.includes(day.getDate()) ||
    disabledDaysOfWeek?.includes(day.getDay())

  useEffect(() => {
    const calendarEvents = events.map((event) => ({ ...event, position: 1 }))
    handleCalendarEventsChange([...calendarEvents])
  }, [events])

  useEffect(() => {
    if (toggleEvents) {
      onEventsChanged?.(sortEvents(calendarEvents))
      setToggleEvents(false)
    }
  }, [toggleEvents])

  return (
    <div className="border border-grey-light rounded-xl overflow-hidden" ref={calendarRef}>
      {/* Header with month and navigation */}
      <FullCalendarHeader
        currentDate={currentDate}
        headerActions={headerActions}
        nextMonth={nextMonth}
        prevMonth={prevMonth}
      />

      {/* Grid for the days of the week */}
      <div className="grid grid-cols-7">
        {daysOfWeek[currentLanguage].map((dayOfWeek) => (
          <div className="px-2 py-4" key={dayOfWeek}>
            <Typography variant="tag" className="text-grey-dark">
              {dayOfWeek}
            </Typography>
          </div>
        ))}
      </div>

      {/* Grid for the days of the month */}
      <div className="grid grid-cols-7 auto-rows-auto overflow-hidden">
        {/* remove the days of the next month */}
        {days.map((day) => (
          <div
            // return the date as string '2021-08-01'
            key={`cell-${format(day, 'yyyy-MM-dd')}`}
            id={`cell-${format(day, 'yyyy-MM-dd')}`} // Add unique id for each day cell
            className={cn(`border border-grey-light relative`, {
              'bg-primary-light': isToday(day) || (!!dragOverCell && isEqual(day, dragOverCell)),
              'bg-white-active': isDisabledDay(day)
            })}
            style={{ minHeight: getWeekHeight(day) }}
            onDragOver={(e) => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              setDragOverCell(day)
              onDragOver(day)
            }} // To allow drop
            onDrop={onDrop} // Handle drop
            onClick={() => {
              if (isDisabledDay(day)) return
              onDaySelected?.(day)
            }} // Handle click
          >
            {/* Day number with red color */}
            <Typography
              variant="tag"
              className={cn('absolute top-1 left-1 text-grey', {
                'text-primary': isToday(day),
                'text-grey-light-active': isBefore(day, monthStart) || isAfter(day, monthEnd)
              })}
            >
              {format(day, 'dd')}
            </Typography>

            {/* Events */}
            {calendarEvents
              .filter((event) => shouldRenderEventSegment(event, day))
              .map((event) => {
                const daysFromStart = differenceInCalendarDays(day, event.startDate)
                const isHoveredEvent = hoveredEventId === event.id
                const segmentEndDate = isBefore(event.endDate, endOfWeek(day, { weekStartsOn: 1 }))
                  ? event.endDate
                  : endOfWeek(day, { weekStartsOn: 1 })
                const segmentDays = differenceInCalendarDays(segmentEndDate, day) + 1
                const isEventStart = compareDates(day, event.startDate)
                const isEventEnd = compareDates(segmentEndDate, event.endDate)
                const weekPosition = getEventWeekPosition(event, day)

                // Render each event once per week span so internal day borders cannot cross labels.
                return daysFromStart >= 0 ? (
                  <TooltipProvider key={event.id} delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          onClick={(e) => {
                            e.stopPropagation()
                            if (suppressClickRef.current) return // click produced by a resize, not a real one
                            onEventClicked?.(event)
                            event?.onClick?.()
                          }}
                          className={cn(
                            `p-2 cursor-move absolute h-12 z-10 flex items-center overflow-hidden transition-colors duration-150`,
                            {
                              'rounded-l-full ml-3': isEventStart,
                              'rounded-r-full': isEventEnd,
                              // once a drag is under way, let the day cells receive dragover.
                              // Driven by dragOverCell (set on the first dragover), not by dragstart:
                              // mutating the dragged pill during dragstart makes Chrome cancel the drag
                              'pointer-events-none': !!dragOverCell
                            }
                          )}
                          style={{
                            width: `calc(${segmentDays * 100}% + ${segmentDays - 1}px)`,
                            top: `${weekPosition * 50 - 20}px`,
                            color: event.style?.color,
                            backgroundColor:
                              isHoveredEvent && event.style?.borderColor
                                ? `color-mix(in srgb, ${event.style.backgroundColor} 82%, ${event.style.borderColor} 18%)`
                                : event.style?.backgroundColor,
                            borderStyle: event.style?.borderStyle,
                            borderColor: event.style?.borderColor,
                            borderTopWidth: '2px',
                            borderBottomWidth: '2px',
                            borderLeftWidth: isEventStart ? '2px' : '0',
                            borderRightWidth: isEventEnd ? '2px' : '0'
                          }}
                          onMouseEnter={() => setHoveredEventId(event.id)}
                          onMouseLeave={() => setHoveredEventId(undefined)}
                          draggable={!event.disableDragging}
                          onDragStart={(e) => {
                            if (isResizingRef.current) {
                              e.preventDefault()
                              return
                            }
                            handleDragStart(e)
                            onDragStart(event, day)
                          }}
                          onDragEnd={() => {
                            // the drop landed outside the grid: onDrop never ran, so reset here
                            setDraggedEvent(undefined)
                            setDraggedDate(undefined)
                            setDragOverCell(undefined)
                          }}
                        >
                          <div className="min-w-0 flex-1 truncate relative z-10">
                            {isEventStart ? event.title : null}
                          </div>
                          {!event.disableExtend && (
                            <div
                              className="absolute top-0 bottom-0 right-0 w-2 cursor-ew-resize bg-red-500"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                startResize(event) // Trigger resizing functionality
                              }}
                            />
                          )}
                        </div>
                      </TooltipTrigger>
                      {event.tooltip && (
                        <TooltipContent
                          side="top"
                          className="p-3 bg-white-hover border border-grey-light rounded-lg"
                        >
                          <Typography variant="secondary-body">{event.tooltip}</Typography>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                ) : null // Don't render the event again in subsequent cells
              })}
          </div>
        ))}
      </div>
    </div>
  )
}

export default FullCalendar
